<template>
  <Modal
    :show="showModal"
    :title="modalTitle"
    :elem-id="elemId"
    :size="size"
    :close-label="backButtonLabel"
    :close-icon="backButtonIcon"
    :save-label="nextButtonLabel"
    :save-icon="nextButtonIcon"
    :save-fn="handleNextButton"
    :save-disabled-fn="isNextDisabled"
    @close="handleBackButton"
    @dismiss="handleDismiss"
  >
    <div class="wizard-modern">
      <div v-if="wizardIcon || wizardSubtitle" class="wizard-modern-intro">
        <div v-if="wizardIcon" class="wizard-modern-icon">
          <i :class="wizardIcon"></i>
        </div>
        <p v-if="wizardSubtitle" class="wizard-modern-subtitle">{{ wizardSubtitle }}</p>
      </div>

      <div class="wizard-modern-body">
        <WizardStepsModern
          :steps="activeSteps"
          :current-step="activeStepIndex"
          :show-progress="showProgress"
          :allow-click="allowStepClick"
          @step-click="handleStepClick"
        />

        <main class="wizard-modern-content" ref="contentRef" @keydown.enter="handleContentEnter">
          <div
            v-if="showSummary && summaryItems.length > 0"
            class="wizard-modern-summary"
            data-testid="wizard-modern-summary"
          >
            <div class="summary-row">
              <div v-for="(item, index) in summaryItems" :key="index" class="summary-item">
                <i v-if="item.icon" :class="item.icon"></i>
                <span class="summary-label">{{ item.label }}:</span>
                <span class="summary-value">{{ item.value }}</span>
              </div>
            </div>
          </div>

          <template v-if="machine">
            <component
              v-if="currentStepComponent"
              :is="currentStepComponent"
              :key="`${activeStepIndex}-${machineState}`"
            />
            <div v-else class="step-loading-placeholder">
              <i class="pi pi-spinner pi-spin"></i>
              <span>{{ t('Carregando...') }}</span>
            </div>
          </template>
          <template v-else>
            <!-- Steps mode: mount EVERY step's slot, show only the active one.
                 Keeping inactive steps in the DOM (v-show, not v-if) mirrors the
                 legacy PrimeVue StepPanels behaviour the wizards were built
                 against — step content (and the data it renders) is available as
                 soon as it loads, without first navigating to that step. -->
            <div
              v-for="(step, idx) in activeSteps"
              :key="step.key ?? idx"
              v-show="idx === activeStepIndex"
              class="wizard-modern-step-pane"
            >
              <slot :name="`step-${idx}`" />
            </div>
          </template>
        </main>
      </div>
    </div>
  </Modal>
</template>

<script setup lang="ts">
import { computed, ref, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import Modal from './Modal.vue';
import WizardStepsModern from './wizard/WizardStepsModern.vue';
import { log } from '@/utils/DebugLogger.ts';

const { t } = useI18n();

type ModalSize = 'small' | 'medium' | 'modalMd' | 'large' | 'xlarge' | 'full' | 'compactDesktop' | 'formOptimized';

interface WizardStepDef {
  key?: string;
  title: string;
}

interface SummaryItem {
  icon?: string;
  label: string;
  value: string | number;
}

interface StateMachine {
  visibleSteps: { value: WizardStepDef[] };
  currentStepIndex: { value: number };
  currentStepComponent?: { value: any };
  currentState: { value: string };
  isLoading: { value: boolean };
  canProceed: { value: boolean };
  isReadonlyMode?: { value: boolean };
  getNextLabelForStep?: (index: number) => string;
  next: () => void;
  back: () => void;
  confirm: () => void;
  cancel: () => void;
  goToStep?: (index: number) => void;
}

interface WizardStep {
  title: string;
  key?: string;
  validate?: () => boolean;
  [key: string]: any;
}

const props = withDefaults(defineProps<{
  // Machine mode (XState)
  machine?: StateMachine;
  // Steps mode (parent-controlled)
  steps?: WizardStep[];
  currentStep?: number;
  // Modal config
  showModal: boolean;
  modalTitle?: string;
  elemId?: string;
  size?: ModalSize;
  // Visual config
  wizardIcon?: string;
  wizardSubtitle?: string;
  showProgress?: boolean;
  allowStepClick?: boolean;
  showSummary?: boolean;
  summaryItems?: SummaryItem[];
  // Validation (steps mode only)
  validateFn?: (stepIndex: number) => boolean;
}>(), {
  modalTitle: '',
  elemId: '',
  size: 'full',
  showProgress: true,
  allowStepClick: false,
  showSummary: false,
  summaryItems: () => [],
});

const emit = defineEmits<{
  close: [];
  cancel: [];
  next: [];
  back: [];
  finish: [];
  'step-click': [stepIndex: number];
}>();

// ── Derived state (works for both modes) ──

const isMachineMode = computed(() => !!props.machine);

const activeSteps = computed(() => {
  if (isMachineMode.value) {
    return (props.machine!.visibleSteps.value || []).map(s => ({ title: s.title, key: s.key }));
  }
  return (props.steps || []).map(s => ({ title: s.title, key: s.key }));
});

const activeStepIndex = computed(() => {
  if (isMachineMode.value) {
    return props.machine!.currentStepIndex.value;
  }
  return props.currentStep ?? 0;
});

const machineState = computed(() => {
  if (isMachineMode.value) {
    return props.machine!.currentState.value;
  }
  return '';
});

const currentStepComponent = computed(() => {
  if (isMachineMode.value) {
    return props.machine!.currentStepComponent?.value || null;
  }
  return null;
});

const isLoading = computed(() => {
  if (isMachineMode.value) {
    return props.machine!.isLoading.value;
  }
  return false;
});

const canProceed = computed(() => {
  if (isMachineMode.value) {
    return props.machine!.canProceed.value;
  }
  if (props.validateFn) {
    return props.validateFn(activeStepIndex.value);
  }
  return true;
});

const isFirstStep = computed(() => activeStepIndex.value === 0);

const isLastStep = computed(() => {
  if (isMachineMode.value) {
    const finalStates = ['summary', 'finished', 'confirming'];
    return finalStates.includes(machineState.value);
  }
  return activeStepIndex.value === activeSteps.value.length - 1;
});

const isFinished = computed(() => {
  if (isMachineMode.value) {
    return machineState.value === 'finished';
  }
  return false;
});

// ── Button labels and icons ──

const backButtonLabel = computed(() => {
  if (isFirstStep.value) return t('Cancelar');
  return t('Voltar');
});

const backButtonIcon = computed(() => {
  if (isFirstStep.value) return 'pi pi-times';
  return 'pi pi-arrow-left';
});

const nextButtonLabel = computed(() => {
  if (isLoading.value) return t('Processando...');
  if (isMachineMode.value && props.machine!.getNextLabelForStep) {
    return props.machine!.getNextLabelForStep(activeStepIndex.value);
  }
  if (isFinished.value) return t('Fechar');
  if (machineState.value === 'summary') return t('Confirmar');
  if (isLastStep.value) return t('Finalizar');
  return t('Próximo');
});

const nextButtonIcon = computed(() => {
  if (isFinished.value || machineState.value === 'summary') return 'pi pi-check';
  if (isLastStep.value) return 'pi pi-check';
  return 'pi pi-arrow-right';
});

// ── Event handlers ──

const handleBackButton = (): void => {
  log('WizardModalModern', 'handleBackButton', 'Back clicked', {
    step: activeStepIndex.value,
    isFirst: isFirstStep.value
  });

  if (isFirstStep.value) {
    handleCancel();
  } else if (isMachineMode.value) {
    props.machine!.back();
  } else {
    emit('back');
  }
};

const handleNextButton = (): void => {
  log('WizardModalModern', 'handleNextButton', 'Next clicked', {
    step: activeStepIndex.value,
    isLast: isLastStep.value
  });

  if (isLoading.value) return;

  if (isFinished.value) {
    handleClose();
    return;
  }

  if (isMachineMode.value && (machineState.value === 'summary' || machineState.value === 'review')) {
    props.machine!.confirm();
    return;
  }

  if (isLastStep.value) {
    emit('finish');
  } else if (isMachineMode.value) {
    props.machine!.next();
  } else {
    emit('next');
  }
};

const isNextDisabled = (): boolean => {
  if (isLoading.value) return true;
  if (isFinished.value) return false;
  if (isMachineMode.value && props.machine!.isReadonlyMode?.value) return true;
  return !canProceed.value;
};

const handleDismiss = (): void => {
  handleCancel();
};

const handleCancel = (): void => {
  if (isMachineMode.value) {
    props.machine!.cancel();
  }
  emit('cancel');
  emit('close');
};

const handleClose = (): void => {
  emit('close');
};

const handleStepClick = (stepIndex: number): void => {
  log('WizardModalModern', 'handleStepClick', 'Step clicked', { stepIndex });
  if (isMachineMode.value && props.machine!.goToStep) {
    props.machine!.goToStep(stepIndex);
  }
  emit('step-click', stepIndex);
};

// ── Generic keyboard ergonomics (apply to every wizard) ──
//
// 1. When the active step changes, auto-focus the first text input in the now-
//    visible step pane so the user can type immediately after pressing Next.
// 2. Pressing Enter inside a text input advances to the next step (same as the
//    Next button), unless it's disabled. Textareas keep Enter = newline.

const contentRef = ref<HTMLElement | null>(null);

// First focusable text-like field. Excludes toggles/file/hidden/disabled inputs;
// textarea is allowed (Enter still inserts a newline there — see handleContentEnter).
const FOCUSABLE_INPUT_SELECTOR =
  'input:not([type="hidden"]):not([type="checkbox"]):not([type="radio"]):not([type="file"]):not([type="button"]):not([type="submit"]):not([disabled]):not([readonly]), textarea:not([disabled]):not([readonly])';

const focusFirstStepInput = (): void => {
  const root = contentRef.value;
  if (!root) return;
  // Steps mode renders every pane (v-show); scope to the visible one so we don't
  // grab a hidden step's input. Machine mode has a single mounted component → root.
  let scope: HTMLElement = root;
  const panes = Array.from(root.querySelectorAll<HTMLElement>('.wizard-modern-step-pane'));
  if (panes.length) {
    const visible = panes.find(p => p.offsetParent !== null);
    if (visible) scope = visible;
  }
  const el = scope.querySelector<HTMLElement>(FOCUSABLE_INPUT_SELECTOR);
  if (el) el.focus();
};

const scheduleFocus = (): void => { nextTick(focusFirstStepInput); };

watch(activeStepIndex, scheduleFocus);
watch(machineState, scheduleFocus);
watch(() => props.showModal, (open) => { if (open) scheduleFocus(); });

const handleContentEnter = (e: KeyboardEvent): void => {
  const target = e.target as HTMLElement | null;
  if (!target || target.tagName !== 'INPUT') return; // textarea → newline; buttons → own action
  const type = (target as HTMLInputElement).type;
  if (['checkbox', 'radio', 'file', 'button', 'submit'].includes(type)) return;
  e.preventDefault();
  if (!isNextDisabled()) handleNextButton();
};

defineExpose({
  activeStepIndex,
  activeSteps
});
</script>

<style scoped>
.wizard-modern {
  display: flex;
  flex-direction: column;
}

.wizard-modern-intro {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid var(--p-surface-200);
}

.wizard-modern-icon {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: rgba(var(--app-primary-color-rgb), 0.1);
  color: var(--app-primary-color);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.wizard-modern-subtitle {
  margin: 0;
  font-size: 0.85rem;
  color: var(--app-text-secondary-color);
}

.wizard-modern-body {
  display: flex;
  gap: 0;
  min-height: 300px;
}

.wizard-modern-content {
  flex: 1;
  padding: 16px 20px;
  overflow-y: auto;
  min-width: 0;
}

.wizard-modern-summary {
  background: var(--p-surface-50);
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 16px;
  border-left: 4px solid var(--app-info-color);
}

.wizard-modern-summary .summary-row {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  align-items: center;
}

.wizard-modern-summary .summary-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.875rem;
}

.wizard-modern-summary .summary-item i {
  font-size: 0.875rem;
  color: var(--app-info-color);
  flex-shrink: 0;
}

.wizard-modern-summary .summary-label {
  font-weight: 600;
  color: var(--app-text-secondary-color);
  white-space: nowrap;
}

.wizard-modern-summary .summary-value {
  font-weight: 500;
  color: var(--app-text-color);
  white-space: nowrap;
}

.step-loading-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 60px 20px;
  color: var(--p-text-muted-color);
  font-size: 1rem;
}

.step-loading-placeholder i {
  font-size: 1.5rem;
  color: var(--app-primary-color);
}

@media (max-width: 768px) {
  .wizard-modern-intro {
    display: none;
  }

  .wizard-modern-body {
    flex-direction: column;
  }

  .wizard-modern-content {
    padding: 16px 12px;
  }
}
</style>
